import { Fragment, useMemo, type ReactNode } from 'react';
import { Separator } from '@voll-intelligence/ui-kit/ui/separator';
import {
  CheckFilterRow,
  FilterSection,
  PriceRangeSection,
  ResultsFiltersPanel,
  type ResultsFiltersPanelHeader,
  SwitchFilterRow,
} from '../../shared/results_page/results_filters_panel.js';
import { TimeRangeSection, type TimeRange } from '../../shared/results_page/time_range_section.js';
import {
  activeBusFilterCount,
  busFilterCatalog,
  EMPTY_BUS_RESULTS_FILTERS,
  matchesBusResultsFilters,
  type BusFilterAxis,
  type BusFilterableOffer,
  type BusResultsFilterValue,
} from './bus_results_rules.js';

export type BusResultsFiltersCopy = {
  filtersLabel: string;
  collapseFiltersLabel: string;
  closeFiltersLabel: string;
  resetFiltersLabel: string;
  policyLabel: string;
  withinPolicyLabel: string;
  /** "Classe de assento". */
  seatClassLabel: string;
  /** O menor preço por pessoa da classe, já formatado ("R$ 450"). */
  fromPriceLabel(amount: number): string;
  /** "Horário", com as réguas "Saída" e "Chegada". */
  timeLabel: string;
  departureLabel: string;
  arrivalLabel: string;
  /** "00:00 - 23:59". */
  timeRangeLabel(range: TimeRange): string;
  /** "Preço até" (decisão da #2440). */
  priceLabel: string;
  minimumPriceLabel: string;
  maximumPriceLabel: string;
  /** "Companhia". */
  companyLabel: string;
};

export type BusResultsFiltersProps = ResultsFiltersPanelHeader & {
  offers: readonly BusFilterableOffer[];
  currency: string;
  value: BusResultsFilterValue;
  copy: BusResultsFiltersCopy;
  locked?: boolean;
  /** Ver `ResultsFiltersPanel.frame`. */
  frame?: boolean;
  onChange(value: BusResultsFilterValue): void;
};

function toggled(list: readonly string[], item: string): string[] {
  return list.includes(item) ? list.filter((candidate) => candidate !== item) : [...list, item];
}

type ChecklistRow = {
  readonly id: string;
  readonly label: string;
  readonly checked: boolean;
  readonly count?: number;
  readonly detail?: string;
};

/** Uma seção de checklist — classe e companhia: a mesma anatomia, o que muda
 *  é a ponta da linha (preço mínimo ou contagem). */
function ChecklistSection({
  title,
  rows,
  locked,
  onToggle,
}: {
  readonly title: string;
  readonly rows: readonly ChecklistRow[];
  readonly locked: boolean;
  readonly onToggle: (id: string) => void;
}): ReactNode {
  if (rows.length === 0) return null;
  return (
    <Fragment>
      <Separator />
      <FilterSection title={title}>
        <div className="-mx-1 grid">
          {rows.map(({ id, ...row }) => (
            <CheckFilterRow
              key={id}
              {...row}
              disabled={locked}
              onToggle={() => {
                onToggle(id);
              }}
            />
          ))}
        </div>
      </FilterSection>
    </Fragment>
  );
}

/** O corpo da coluna: os blocos na ordem do frame — Política → Classe de
 *  assento (com o menor preço de cada uma) → Horário → Preço até → Companhia. */
function BusResultsFiltersBody({
  offers,
  currency,
  value,
  copy,
  locked,
  onChange,
}: Required<
  Pick<BusResultsFiltersProps, 'offers' | 'currency' | 'value' | 'copy' | 'locked' | 'onChange'>
>): ReactNode {
  const { priceFloor, priceCeiling, seatClasses, companies } = useMemo(
    () => busFilterCatalog(offers, value),
    [offers, value],
  );
  const countMatching = (
    axis: BusFilterAxis,
    matchesAxis: (facts: BusFilterableOffer) => boolean,
  ) =>
    offers.filter((facts) => matchesBusResultsFilters(facts, value, axis) && matchesAxis(facts))
      .length;
  return (
    <Fragment>
      <FilterSection title={copy.policyLabel}>
        <SwitchFilterRow
          label={copy.withinPolicyLabel}
          checked={value.policyOnly}
          disabled={locked}
          onChange={(policyOnly) => {
            onChange({ ...value, policyOnly });
          }}
        />
      </FilterSection>
      <ChecklistSection
        title={copy.seatClassLabel}
        rows={seatClasses.map(({ id, label, fromPrice }) => ({
          id,
          label,
          checked: value.seatClassIds.includes(id),
          ...(fromPrice === undefined ? {} : { detail: copy.fromPriceLabel(fromPrice) }),
        }))}
        locked={locked}
        onToggle={(id) => {
          onChange({ ...value, seatClassIds: toggled(value.seatClassIds, id) });
        }}
      />
      <Separator />
      <TimeRangeSection
        title={copy.timeLabel}
        rangeLabel={(range) => copy.timeRangeLabel(range)}
        locked={locked}
        rows={[
          {
            label: copy.departureLabel,
            value: value.departure,
            onChange: (departure) => {
              onChange({ ...value, departure });
            },
          },
          {
            label: copy.arrivalLabel,
            value: value.arrival,
            onChange: (arrival) => {
              onChange({ ...value, arrival });
            },
          },
        ]}
      />
      {priceFloor !== undefined && priceCeiling !== undefined && (
        <Fragment>
          <Separator />
          <PriceRangeSection
            title={copy.priceLabel}
            floor={priceFloor}
            ceiling={priceCeiling}
            currency={currency}
            value={{
              ...(value.minimumPrice === undefined ? {} : { minimum: value.minimumPrice }),
              ...(value.maximumPrice === undefined ? {} : { maximum: value.maximumPrice }),
            }}
            minimumLabel={copy.minimumPriceLabel}
            maximumLabel={copy.maximumPriceLabel}
            locked={locked}
            onChange={({ minimum, maximum }) => {
              onChange({ ...value, minimumPrice: minimum, maximumPrice: maximum });
            }}
          />
        </Fragment>
      )}
      <ChecklistSection
        title={copy.companyLabel}
        rows={companies.map(({ id, label }) => ({
          id,
          label,
          checked: value.companyIds.includes(id),
          count: countMatching('companyIds', (facts) => facts.companyId === id),
        }))}
        locked={locked}
        onToggle={(id) => {
          onChange({ ...value, companyIds: toggled(value.companyIds, id) });
        }}
      />
    </Fragment>
  );
}

/**
 * A coluna de cortes do ônibus — o painel da casca (`ResultsFiltersPanel`, o
 * layout da #2351) com os eixos do frame de disponibilidade (#2440). Todos os
 * cortes rodam no cliente sobre a lista completa do dia.
 */
export function BusResultsFilters({
  offers,
  currency,
  value,
  copy,
  locked = false,
  onChange,
  ...panel
}: Readonly<BusResultsFiltersProps>): ReactNode {
  return (
    <ResultsFiltersPanel
      {...panel}
      copy={copy}
      active={activeBusFilterCount(value) > 0}
      locked={locked}
      onReset={() => {
        onChange(EMPTY_BUS_RESULTS_FILTERS);
      }}
    >
      <BusResultsFiltersBody
        offers={offers}
        currency={currency}
        value={value}
        copy={copy}
        locked={locked}
        onChange={onChange}
      />
    </ResultsFiltersPanel>
  );
}
